// RB-MY01-PalmGarden.web/src/components/AddPlantForm.tsx
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/add-plant-form.css';
import { useNavigate } from 'react-router-dom';

interface PlantData {
  id: string;
  name: string;
  species: string;
  commonName?: string;
  images: string[];
  notes?: string;
  description?: string;
  origin?: string;
  growingConditions?: string;
  careInstructions?: string;
  tags?: string[];
  published: boolean;
}

interface AddPlantFormProps {
  onAddPlant: (plant: Omit<PlantData, 'id'>) => void;
  onCancel: () => void;
  availableImages?: string[];
}

// Define an interface for the upload response
interface UploadResponse {
  message: string;
  url?: string;
  urls?: string[];
  success?: boolean;
}

const AddPlantForm: React.FC<AddPlantFormProps> = ({ 
  onAddPlant, 
  onCancel,
  availableImages = [] 
}) => {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('basic');
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const [plant, setPlant] = useState<Omit<PlantData, 'id'>>({
    name: '',
    species: '',
    commonName: '',
    images: [],
    notes: '',
    description: '',
    origin: '',
    growingConditions: '',
    careInstructions: '',
    tags: [],
    published: false
  });

  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setPlant({ ...plant, [name]: value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setPlant({ ...plant, [name]: checked });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('images', e.target.files[0]);
      
      console.log('Uploading image to:', 'http://localhost:8080/api/upload');
      
      const response = await fetch('http://localhost:8080/api/upload', {
        method: 'POST',
        body: formData
      });
      
      const text = await response.text();
      console.log('Raw response:', text);
      
      let data: UploadResponse;
      try {
        data = JSON.parse(text) as UploadResponse;
      } catch (e) {
        throw new Error('Invalid JSON response: ' + text.substring(0, 100));
      }
      
      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }
      
      console.log('Upload successful:', data);
      
      // Update state with the new image URL
      if (data.url) {
        setUploadedImages(prev => [...prev, data.url as string]);
        setPlant(prev => ({
          ...prev,
          images: [...prev.images, data.url as string]
        }));
      } else if (data.urls && data.urls.length > 0) {
        const validUrls = data.urls.filter(url => url !== undefined) as string[];
        setUploadedImages(prev => [...prev, ...validUrls]);
        setPlant(prev => ({
          ...prev,
          images: [...prev.images, ...validUrls]
        }));
      }
      
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(`Failed to upload image: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setUploading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() === '') return;
    
    if (!plant.tags) {
      setPlant({ ...plant, tags: [tagInput.trim()] });
    } else {
      setPlant({ ...plant, tags: [...plant.tags, tagInput.trim()] });
    }
    
    setTagInput('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    if (!plant.tags) return;
    
    setPlant({
      ...plant,
      tags: plant.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleRemoveImage = (imageToRemove: string) => {
    setPlant({
      ...plant,
      images: plant.images.filter(image => image !== imageToRemove)
    });
    setUploadedImages(uploadedImages.filter(image => image !== imageToRemove));
  };

  const handleSelectExistingImage = (url: string) => {
    setPlant(prev => ({
      ...prev,
      images: [...prev.images, url]
    }));
    setUploadedImages(prev => [...prev, url]);
    setShowImageLibrary(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!plant.name || !plant.species) {
      alert('Name and species are required!');
      return;
    }
    
    try {
      // Prepare the plant data
      const plantToSubmit = {
        ...plant,
        images: uploadedImages
      };
      
      console.log('Submitting plant:', plantToSubmit);
      
      // Call the onAddPlant function
      await onAddPlant(plantToSubmit);
      
      // Show success message directly in the form using only React state
      setSuccessMessage('Plant added successfully!');
      
      // Clear the form after 2 seconds
      setTimeout(() => {
        onCancel();
      }, 2000);
    } catch (error) {
      console.error('Error in form submission:', error);
      alert(`Failed to add plant: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  return (
    <div className="add-plant-form-container">
      <h2>Add New Plant</h2>
      
      <div className="form-tabs">
        <button 
          className={activeTab === 'basic' ? 'active' : ''} 
          onClick={() => setActiveTab('basic')}
        >
          Basic Info
        </button>
        <button 
          className={activeTab === 'details' ? 'active' : ''} 
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button 
          className={activeTab === 'images' ? 'active' : ''} 
          onClick={() => setActiveTab('images')}
        >
          Images
        </button>
        <button 
          className={activeTab === 'care' ? 'active' : ''} 
          onClick={() => setActiveTab('care')}
        >
          Care
        </button>
      </div>
      
      <form onSubmit={handleSubmit}>
        {activeTab === 'basic' && (
          <div className="form-tab-content">
            <div className="form-group">
              <label htmlFor="name">Scientific Name*</label>
              <input
                type="text"
                id="name"
                name="name"
                value={plant.name}
                onChange={handleChange}
                required
                placeholder="e.g., Abelmoschus esculentus"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="commonName">Common Name</label>
              <input
                type="text"
                id="commonName"
                name="commonName"
                value={plant.commonName || ''}
                onChange={handleChange}
                placeholder="e.g., Okra, Lady's Finger"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="species">Species*</label>
              <input
                type="text"
                id="species"
                name="species"
                value={plant.species}
                onChange={handleChange}
                required
                placeholder="e.g., Okra (Lady's Finger)"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="origin">Origin</label>
              <input
                type="text"
                id="origin"
                name="origin"
                value={plant.origin || ''}
                onChange={handleChange}
                placeholder="e.g., Africa, Asia"
              />
            </div>
            
            <div className="form-group tags-input">
              <label>Tags</label>
              <div className="tag-input-container">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <button type="button" onClick={handleAddTag}>Add</button>
              </div>
              
              <div className="tags-list">
                {plant.tags && plant.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'details' && (
          <div className="form-tab-content">
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={plant.description || ''}
                onChange={handleChange}
                rows={5}
                placeholder="Detailed description of the plant..."
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={plant.notes || ''}
                onChange={handleChange}
                rows={3}
                placeholder="Additional notes about the plant..."
              />
            </div>
          </div>
        )}
        
        {activeTab === 'images' && (
          <div className="form-tab-content">
            <div className="form-group">
              <label>Upload Images (Max 5)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={uploading}
              />
              {uploading && <p className="uploading-message">Uploading images...</p>}
            </div>
            
            <div className="form-group">
              <button 
                type="button" 
                className="secondary-button"
                onClick={() => setShowImageLibrary(!showImageLibrary)}
              >
                {showImageLibrary ? 'Hide Image Library' : 'Select from Image Library'}
              </button>
              
              {showImageLibrary && (
                <div className="image-library">
                  <h4>Select from existing images</h4>
                  <div className="uploaded-images">
                    {availableImages.map((url, index) => (
                      <div key={index} className="library-image">
                        <img src={url} alt={`Library ${index}`} />
                        <button 
                          type="button"
                          onClick={() => handleSelectExistingImage(url)}
                        >
                          Select
                        </button>
                      </div>
                    ))}
                    {availableImages.length === 0 && (
                      <p>No images available in the library</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Current Images</label>
              <div className="uploaded-images">
                {uploadedImages.length > 0 ? (
                  uploadedImages.map((url, index) => (
                    <div key={index} className="uploaded-image">
                      <img src={url} alt={`Uploaded ${index}`} />
                      <button 
                        type="button" 
                        onClick={() => handleRemoveImage(url)}
                        className="remove-image-button"
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <p>No images uploaded yet</p>
                )}
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'care' && (
          <div className="form-tab-content">
            <div className="form-group">
              <label htmlFor="growingConditions">Growing Conditions</label>
              <textarea
                id="growingConditions"
                name="growingConditions"
                value={plant.growingConditions || ''}
                onChange={handleChange}
                rows={3}
                placeholder="Light, soil, temperature requirements..."
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="careInstructions">Care Instructions</label>
              <textarea
                id="careInstructions"
                name="careInstructions"
                value={plant.careInstructions || ''}
                onChange={handleChange}
                rows={4}
                placeholder="Detailed care instructions..."
              />
            </div>
          </div>
        )}
        
        <div className="form-actions">
          {isAdmin && (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="published"
                  checked={plant.published}
                  onChange={handleCheckboxChange}
                />
                Publish Immediately
              </label>
            </div>
          )}
          
          <div className="form-buttons">
            <button type="button" onClick={onCancel} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="submit-button">
              Add Plant
            </button>
          </div>
        </div>
      </form>

      {process.env.NODE_ENV === 'development' && (
        <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <h4>Debug Info:</h4>
          <p>Uploaded Images: {uploadedImages.length}</p>
          <ul>
            {uploadedImages.map((url, i) => (
              <li key={i}>{url}</li>
            ))}
          </ul>
          <p>Plant Images: {plant.images.length}</p>
          <ul>
            {plant.images.map((url, i) => (
              <li key={i}>{url}</li>
            ))}
          </ul>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          {successMessage}
        </div>
      )}
    </div>
  );
};

export default AddPlantForm; 